import * as React from "react";
import { memo, useCallback, useEffect } from "react";
import { Fade, Paper, styled } from "@mui/material";
import { NavBarZIndex } from "fe-common/ui/stacking";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import {
  PatientCaseGrouping,
  useDiagnosticAgentDDRequired,
  useDiagnosticAgentState,
} from "../../../context/diagnosticAgent";
import { useHistory, useLocation, useRouteMatch } from "react-router";
import { removeLastRoute } from "../../../util/pathUtils";
import { AgentRoute } from "../util/agentRoutes";
import {
  RosebudRouteSetting,
  RosebudRoutesSettings,
} from "../util/rosebudRoutesSettings";
import {
  MobileDiagnoseButton,
  MobileDiagnoseButtonContainer,
  RosebudMobileBottomNavigationHeight,
} from "../common/rosebudButtomNavigationButton";
import {
  bottomNavigationMenuHeight,
  DataInputMobileSidePadding,
} from "../common/styles/consts";
import { PatientChartSectionTitle } from "../patientChart/PatientChartSectionTitle";
import { PatientChartGroupedFeatures } from "../patientChart/patientChartGroupedFeatures";

import { useRouteHelper } from "../useRouteHelper";
import { PatientChartAgeGender } from "../patientChart/patientChartAgeGender";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { useDataInputPanels } from "../useDataInputPanels";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";

//region [[ Styles ]]
const MobileExpandedContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  position: "relative",
  margin: "-7px", // Kill the contentWrapper 7px padding
});

const MobilePatientView = styled("div")({
  // make room for the navigation buttons
  minHeight: `calc(100vh - 65px)`,
  width: "100%",
  zIndex: NavBarZIndex - 10,
  paddingBottom: `${RosebudMobileBottomNavigationHeight + 5}px`,
  boxShadow: "none",
  position: "absolute",
});

export const MobileDiagnoseButtonContainerStyled = styled(
  MobileDiagnoseButtonContainer,
)({
  bottom: `${bottomNavigationMenuHeight}px`,
  paddingRight: "24px",
  paddingLeft: "24px",
});

const PaddingPlaceHolder = styled("div")({
  content: " ",
  height: `${RosebudMobileBottomNavigationHeight}px`,
});

const MobilePatientSectionContainer = styled(Paper)(
  ({ theme, selected }: { theme?: any; selected?: boolean }) => ({
    marginBottom: "10px",
    backgroundColor: "white",
    [theme.breakpoints.down(MobileView)]: {
      paddingRight: `${DataInputMobileSidePadding}px`,
      paddingLeft: `${DataInputMobileSidePadding}px`,
      marginBottom: "0px",
    },
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]

export interface MobilePatientCaseProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const MobilePatientCase = memo(
  ({ ...props }: MobilePatientCaseProps) => {
    const [, setIsDDRequired] = useDiagnosticAgentDDRequired();
    const match = useRouteMatch();
    const history = useHistory();

    const { navigateTo } = useRouteHelper();
    const location = useLocation();
    const lastRoute = location.pathname.split("/")[2];
    const currentStep = RosebudRoutesSettings.find(
      (s) => lastRoute.indexOf(s.route) !== -1,
    );

    // Clean last route from path to assign a new one
    const matchUrl = removeLastRoute(match.url);

    let [state] = useDiagnosticAgentState();

    const ddRoute = RosebudRoutesSettings.find(
      (r) => r.route === AgentRoute.DD,
    );

    const handleCloseClicked = useCallback(() => {
      evt({
        category: CATEGORY.AGENT_PATIENT_VIEW,
        action: ACTION.COLLAPSE_PATIENT,
      });
      // TODO: get queryParams.goTo

      history.push(`${matchUrl}/${ddRoute.route}`);
    }, [matchUrl, ddRoute, history]);

    const handleDiagnoseClicked = useCallback(() => {
      evt({
        category: CATEGORY.AGENT_PATIENT_VIEW,
        action: ACTION.DIAGNOSE,
        fb: false,
      });
      handleCloseClicked();
    }, [handleCloseClicked]);

    // Make sure to stop calculating the DD
    useEffect(() => {
      setIsDDRequired(false);
    }, [setIsDDRequired]);

    const sections = RosebudRoutesSettings.filter(
      (s, index) =>
        s.grouping &&
        s.grouping !== PatientCaseGrouping.VITAL_SIGNS &&
        s.isWizardStep,
    );

    const handleChartSectionTitleClick = useCallback(
      (step: RosebudRouteSetting, subGroupId?: string) => () => {
        handleCloseClicked();
        navigateTo({ step: step, grouping: step.grouping, subGroupId });
      },
      [navigateTo, handleCloseClicked],
    );

    const { loading } = useDataInputPanels();

    return (
      <Fade timeout={500} in mountOnEnter unmountOnExit>
        <MobileExpandedContainer>
          <MobilePatientView>
            <PatientChartAgeGender />

            {loading && <ThreeDotsLoader />}
            {!loading &&
              sections.map((step, index) => (
                <MobilePatientSectionContainer
                  elevation={0}
                  key={`patient-section-${step.route}`}
                >
                  <PatientChartSectionTitle
                    titleText={step.displayText}
                    onClick={handleChartSectionTitleClick(step)}
                    selected={step.route === currentStep?.route}
                  />
                  <PatientChartGroupedFeatures
                    section={step.grouping}
                    step={step}
                    onClick={handleCloseClicked}
                    showAllSubGroups={true}
                  />
                </MobilePatientSectionContainer>
              ))}

            {state.dirty && (
              <>
                <PaddingPlaceHolder />
                <MobileDiagnoseButtonContainerStyled>
                  <MobileDiagnoseButton onClick={handleDiagnoseClicked}>
                    {ddRoute.icon}
                    {"Update"}
                  </MobileDiagnoseButton>
                </MobileDiagnoseButtonContainerStyled>
              </>
            )}
          </MobilePatientView>
        </MobileExpandedContainer>
      </Fade>
    );
  },
);
